import type { GameMode } from '@interactive-room';
import {
  deleteActivityDraft,
  getActivityDraft,
  listActivityDrafts,
  saveActivityDraft,
  createDraftId,
  type ActivityDraft,
} from './activityStorage';
import {
  createExamId,
  deleteEvaluationExam,
  getEvaluationExam,
  listEvaluationExams,
  saveEvaluationExam,
} from './examStorage';
import { matchesSubjectTag } from './constants/subjectCatalog';
import {
  matchesLinkedSession,
  type LinkedSessionRef,
} from './types/linkedSessionContext';

export type StudioResourceKind = 'activity' | 'exam' | 'guide' | 'class';
export type StudioResourceStatus = 'draft' | 'published';

export interface StudioResource {
  id: string;
  kind: StudioResourceKind;
  title: string;
  subjectTag: string;
  updatedAt: number;
  status: StudioResourceStatus;
  mode?: GameMode;
  blockCount?: number;
  accessCode?: string;
  programmedAt?: number;
  courseId?: string;
  linkedSession?: LinkedSessionRef;
}

function matchesCourseId(resourceCourseId: string | undefined, courseId: string): boolean {
  return resourceCourseId === courseId;
}

export function listResourcesForCourse(courseId: string): StudioResource[] {
  const activities = listActivityDrafts()
    .filter((draft) => matchesCourseId(draft.courseId, courseId))
    .map(activityToResource);

  const exams = listEvaluationExams()
    .filter((exam) => matchesCourseId(exam.courseId, courseId))
    .map(examToResource);

  return [...activities, ...exams].sort((a, b) => b.updatedAt - a.updatedAt);
}

export function countResourcesForCourse(courseId: string): number {
  return listResourcesForCourse(courseId).length;
}

export function listResourcesForSession(
  courseId: string,
  unitId: string,
  sessionId: string,
): StudioResource[] {
  return listResourcesForCourse(courseId).filter((resource) =>
    matchesLinkedSession(resource.linkedSession, courseId, unitId, sessionId),
  );
}

export function listResourcesForSubject(subjectId: string): StudioResource[] {
  const activities = listActivityDrafts()
    .filter((draft) => matchesSubjectTag(draft.subjectTag, subjectId))
    .map(activityToResource);

  const exams = listEvaluationExams()
    .filter((exam) => matchesSubjectTag(exam.subjectTag, subjectId))
    .map(examToResource);

  return [...activities, ...exams].sort((a, b) => b.updatedAt - a.updatedAt);
}

export function countResourcesForSubject(subjectId: string): number {
  return listResourcesForSubject(subjectId).length;
}

function activityToResource(draft: ActivityDraft): StudioResource {
  return {
    id: draft.id,
    kind: 'activity',
    title: draft.title,
    subjectTag: draft.subjectTag,
    updatedAt: draft.updatedAt,
    status: 'draft',
    mode: draft.mode,
    courseId: draft.courseId,
    linkedSession: draft.linkedSession,
  };
}

function examToResource(exam: import('@interactive-room').EvaluationExam): StudioResource {
  const profile =
    exam.deliveryProfile ??
    (exam.resourceType === 'guide'
      ? 'learning_guide'
      : exam.deliveryFormat === 'interactive'
        ? 'interactive_class'
        : 'assessment');
  const kind =
    profile === 'learning_guide' ? 'guide' : profile === 'interactive_class' ? 'class' : 'exam';
  return {
    id: exam.id,
    kind,
    title: exam.title,
    subjectTag: exam.subjectTag,
    updatedAt: exam.updatedAt,
    status: exam.programmedAt && exam.accessCode ? 'published' : 'draft',
    blockCount: exam.blocks.length,
    accessCode: exam.accessCode,
    programmedAt: exam.programmedAt,
    courseId: exam.courseId,
    linkedSession: exam.linkedSession,
  };
}

export function duplicateActivityResource(id: string): ActivityDraft | null {
  const source = getActivityDraft(id);
  if (!source) return null;
  const copy: ActivityDraft = {
    ...source,
    id: createDraftId(),
    title: `${source.title} (copia)`,
    updatedAt: Date.now(),
    payload: structuredClone(source.payload),
  };
  return saveActivityDraft(copy);
}

export function duplicateExamResource(id: string): import('@interactive-room').EvaluationExam | null {
  const source = getEvaluationExam(id);
  if (!source) return null;
  const now = Date.now();
  const copy = {
    ...source,
    id: createExamId(),
    title: `${source.title} (copia)`,
    createdAt: now,
    updatedAt: now,
    accessCode: undefined,
    asyncRoomId: undefined,
    asyncRoomJoinCode: undefined,
    programmedAt: undefined,
    blocks: source.blocks.map((block) => ({
      ...block,
      id: `blk_${now.toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      payload: structuredClone(block.payload),
    })),
  };
  return saveEvaluationExam(copy);
}

export function deleteResource(resource: StudioResource): void {
  if (resource.kind === 'activity') deleteActivityDraft(resource.id);
  else deleteEvaluationExam(resource.id);
}

export function getActivityDraftForResource(id: string): ActivityDraft | null {
  return getActivityDraft(id);
}

export function getExamForResource(id: string): import('@interactive-room').EvaluationExam | null {
  return getEvaluationExam(id);
}
